import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { getDesignFiles } from "../../../../services/designService";
import ImageGallery from "./ImageGallery";
import Inspirations from "./Inspirations";
import Concepts from "./Concepts";
import Renders from "./Renders";
import ShopDrawings from "./ShopDrawings";

const TAGS = {
  FLOORPLAN_AS_BUILT: "FLOORPLAN_AS_BUILT",
  FLOORPLAN_TARGET: "FLOORPLAN_TARGET",
  ELEVATION: "ELEVATION",
};

const TAB_DEFS = [
  { id: "inspirations", label: "Inspirations" },
  { id: "concepts", label: "Concepts", fullOnly: true },
  { id: "renders", label: "Renders", fullOnly: true },
  { id: "materials", label: "Materials", fullOnly: true },
  { id: "floorplans", label: "Floorplans" },
  { id: "shopDrawings", label: "Shop Drawings", fullOnly: true },
];

const FULL_STAGES = new Set(["PROPOSAL", "PROJECT", "READY_TO_START"]);

const getVisibleTabs = (stage) => {
  if (stage && FULL_STAGES.has(stage)) return TAB_DEFS;
  return TAB_DEFS.filter((t) => !t.fullOnly);
};

const Design = ({ details, isMobile: isMobileProp }) => {
  const theme = useTheme();
  const detectedMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile =
    typeof isMobileProp === "boolean" ? isMobileProp : detectedMobile;

  const projectId = details?.id;
  const stage = details?.stage;
  const tabs = useMemo(() => getVisibleTabs(stage), [stage]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "inspirations");
  const [images, setImages] = useState({
    floorplanAsBuilt: [],
    floorplanTarget: [],
    elevation: [],
  });
  const [galleryLoading, setGalleryLoading] = useState(false);

  useEffect(() => {
    if (!tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "inspirations");
    }
  }, [tabs, activeTab]);

  // Only the gallery-shaped sub-tabs (floorplans + materials) need the
  // tag-based file fetch — concepts / renders / shop-drawings / inspirations
  // each fetch their own data internally.
  useEffect(() => {
    if (!projectId) return;
    if (activeTab !== "floorplans" && activeTab !== "materials") return;
    let cancelled = false;
    const fetchTag = async (tag) => {
      try {
        const r = await getDesignFiles(projectId, tag);
        if (r?.success && Array.isArray(r.data)) return r.data;
        return [];
      } catch {
        return [];
      }
    };
    (async () => {
      setGalleryLoading(true);
      try {
        const [asBuilt, target, elevation] = await Promise.all([
          fetchTag(TAGS.FLOORPLAN_AS_BUILT),
          fetchTag(TAGS.FLOORPLAN_TARGET),
          fetchTag(TAGS.ELEVATION),
        ]);
        if (cancelled) return;
        setImages({
          floorplanAsBuilt: asBuilt,
          floorplanTarget: target,
          elevation,
        });
      } finally {
        if (!cancelled) setGalleryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, activeTab]);

  const renderActive = () => {
    if (activeTab === "inspirations") {
      return <Inspirations projectId={projectId} />;
    }
    if (activeTab === "concepts") {
      return <Concepts projectId={projectId} />;
    }
    if (activeTab === "renders") {
      return <Renders projectId={projectId} />;
    }
    if (activeTab === "shopDrawings") {
      return <ShopDrawings projectId={projectId} />;
    }
    if (galleryLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (activeTab === "floorplans") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <ImageGallery
            title="As-Built Floorplans"
            description="Floorplans of the existing space."
            tag={TAGS.FLOORPLAN_AS_BUILT}
            images={images.floorplanAsBuilt}
          />
          <ImageGallery
            title="Target Floorplans"
            description="Floorplans of the proposed design."
            tag={TAGS.FLOORPLAN_TARGET}
            images={images.floorplanTarget}
          />
        </Box>
      );
    }
    if (activeTab === "materials") {
      return (
        <ImageGallery
          title="Materials"
          description="Material and elevation references for this project."
          tag={TAGS.ELEVATION}
          images={images.elevation}
        />
      );
    }
    return null;
  };

  const sidebar = (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "row" : "column",
        gap: 1,
        flexShrink: 0,
        overflowX: isMobile ? "auto" : "visible",
        width: isMobile ? "100%" : 224,
        pb: isMobile ? 1 : 0,
        pr: isMobile ? 0 : 2,
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <Box
            key={tab.id}
            component="button"
            type="button"
            onClick={() => setActiveTab(tab.id)}
            sx={{
              border: "none",
              cursor: "pointer",
              fontFamily: "Inter Tight",
              fontSize: "14px",
              fontWeight: 400,
              padding: "10px 16px",
              borderRadius: isMobile ? "999px" : "10px",
              whiteSpace: "nowrap",
              textAlign: "left",
              backgroundColor: isActive ? "#4D6BF6" : "transparent",
              color: isActive ? "#fff" : "#0D0D0E",
              "&:hover": {
                backgroundColor: isActive ? "#4D6BF6" : "#F3F4F6",
              },
              flexShrink: 0,
            }}
          >
            {tab.label}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 2 : 0,
        width: "100%",
        height: "100%",
        p: 1,
      }}
    >
      {sidebar}
      <Box sx={{ flex: 1, minWidth: 0 }}>{renderActive()}</Box>
    </Box>
  );
};

export default Design;
